import { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText, Printer, Mail, Edit2, X, ChevronDown, Eye, Send } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore } from '@/store/auth.store';
import {
  TAMANO_INFO, DISENO_INFO, getDimensiones,
  type TemplateData,
} from '../configuracion/templates/template-types';
import { TemplateRenderer } from '../configuracion/templates/TemplateRenderer';
import { usePlantillasConfig, DEFAULT_PLANTILLAS_CONFIG } from '../configuracion/templates/usePlantillaConfig';
import { RecetaWizard } from './RecetaWizard';
import { getErrorMessage } from '@/lib/errors';
import { useConfiguracionClinica, moduloAplica } from '@/hooks/useConfiguracionClinica';
import { useFacultadPrescribir } from '@/hooks/useFacultadPrescribir';

// ─── Types ───────────────────────────────────────────────────────────────────

type RecetaStatus = 'borrador' | 'activa' | 'inactiva' | 'cancelada';

const STATUS_INFO: Record<RecetaStatus, { label: string; cls: string }> = {
  borrador:  { label: 'Borrador',  cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  activa:    { label: 'Activa',    cls: 'bg-green-50 text-green-700 border-green-200' },
  inactiva:  { label: 'Inactiva',  cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  cancelada: { label: 'Cancelada', cls: 'bg-red-50 text-red-500 border-red-200' },
};

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Print Modal ─────────────────────────────────────────────────────────────

function RecetaPrintModal({ receta, paciente, clinica, usuario, onClose }: {
  receta: any; paciente: any; clinica: any; usuario: any; onClose: () => void;
}) {
  const printRef = useRef<HTMLDivElement>(null);
  const { data: plantillasConfig } = usePlantillasConfig();
  const cfg = (plantillasConfig ?? DEFAULT_PLANTILLAS_CONFIG).receta;
  const { width: pw, height: ph, cssSize } = getDimensiones(cfg.tamanoHoja, cfg.orientacion);
  const SCALE = 0.72;
  const modalW = Math.round(pw * SCALE);
  const modalH = Math.round(ph * SCALE);

  const nombre = [paciente?.nombre, paciente?.apellidoPaterno, paciente?.apellidoMaterno].filter(Boolean).join(' ');

  const data: TemplateData = {
    tipo: 'receta',
    folio: receta.folio || '—',
    fecha: formatDate(receta.fecha || new Date()),
    qrUrl: receta.folio ? `${window.location.origin}/receta-digital/${receta.folio}` : undefined,
    generadoPor: receta.medico?.nombre || usuario?.nombre,
    clinica: {
      nombre:    clinica?.nombre    || 'Clínica',
      cedula:    clinica?.cedulaFiscal || '',
      direccion: clinica?.direccion || '',
      telefono:  clinica?.telefono  || '',
    },
    medico: {
      nombre:       receta.medico?.nombre || usuario?.nombre || 'Médico',
      cedula:       receta.medico?.cedula || '',
      especialidad: receta.medico?.especialidad || '',
    },
    paciente: {
      nombre,
      fechaNacimiento: paciente?.fechaNacimiento ? formatDate(paciente.fechaNacimiento) : '',
      sexo: paciente?.genero ? ({ masculino: 'Masculino', femenino: 'Femenino', otro: 'Otro' }[paciente.genero as string] || '') : '',
    },
    fechaConsulta: formatDate(receta.fecha || new Date()),
    medicaciones: (receta.medicaciones || []).map((m: any) => ({
      denominacionGenerica: m.denominacionGenerica,
      denominacionDistintiva: m.denominacionDistintiva || undefined,
      dosis: m.dosis,
      frecuencia: m.frecuencia,
      duracion: m.duracion,
      viaAdministracion: m.viaAdministracion || undefined,
      indicaciones: m.indicaciones,
    })),
    instrucciones: receta.notas || undefined,
  };

  function handlePrint() {
    const el = printRef.current;
    if (!el) return;
    const css = `*{margin:0;padding:0;box-sizing:border-box}body{background:#fff}@media print{@page{size:${cssSize};margin:0}}`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><style>${css}</style></head><body>${DOMPurify.sanitize(el.innerHTML)}</body></html>`);
    w.document.close();
    w.onload = () => w.print();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-5xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 md:px-6 py-3 md:py-4 bg-white rounded-t-2xl border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-sm text-text-primary">{receta.folio}</h2>
            <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border',
              (STATUS_INFO[(receta.status as RecetaStatus)] ?? STATUS_INFO.inactiva).cls
            )}>{(STATUS_INFO[(receta.status as RecetaStatus)] ?? STATUS_INFO.inactiva).label}</span>
            <span className="text-xs text-text-secondary bg-gray-100 px-2 py-0.5 rounded-full">{TAMANO_INFO[cfg.tamanoHoja].label}</span>
            <span className="text-xs text-text-secondary bg-gray-100 px-2 py-0.5 rounded-full">{DISENO_INFO[cfg.diseno].label}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {receta.status !== 'cancelada' && (
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition-colors">
                <Printer size={12} /> Imprimir
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Template preview */}
        <div className="p-3 md:p-6 overflow-x-auto">
          <div style={{ width: modalW, height: modalH, overflow: 'hidden', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', borderRadius: 4, margin: '0 auto' }}>
            <div ref={printRef} style={{ width: pw, height: ph, transform: `scale(${SCALE})`, transformOrigin: 'top left', pointerEvents: 'none', userSelect: 'none' }}>
              <TemplateRenderer data={data} diseno={cfg.diseno} width={pw} height={ph} disenoPersonalizadoHtml={cfg.disenoPersonalizadoHtml} />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
          <p className="text-xs text-text-secondary">Plantilla configurada en Configuración → Plantillas</p>
          <button onClick={() => { toast.info('Envío por correo electrónico próximamente'); }}
            className="flex items-center gap-1.5 text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-text-secondary transition-colors">
            <Mail size={12} /> Enviar por correo
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

interface Props {
  id: string;
  canEdit: boolean;
  paciente?: any;
  noDelete?: boolean;
  citaId?: string;
  triggerAdd?: number;
}

export function RecetasTab({ id, canEdit, paciente, noDelete, citaId, triggerAdd }: Props) {
  const { usuario } = useAuthStore();
  const qc = useQueryClient();
  const { perfilTipo, modulosHabilitados } = useConfiguracionClinica();
  const { tieneFacultadPrescribir } = useFacultadPrescribir();
  const puedeEmitir = canEdit && moduloAplica('recetas_emitir', perfilTipo, modulosHabilitados) && tieneFacultadPrescribir;

  const [filterStatus, setFilterStatus] = useState<RecetaStatus | ''>('');
  const [filterDesde,  setFilterDesde]  = useState('');
  const [filterHasta,  setFilterHasta]  = useState('');
  const [wizardOpen,   setWizardOpen]   = useState(false);
  const [editingId,    setEditingId]    = useState<string | null>(null);
  const [printReceta,  setPrintReceta]  = useState<any | null>(null);
  useEffect(() => { if (triggerAdd && puedeEmitir) setWizardOpen(true); }, [triggerAdd, puedeEmitir]);

  // Fetch clinic info for print modal
  const { data: clinica } = useQuery({
    queryKey: ['clinica', usuario?.clinicaId],
    queryFn: () => api.get(`/api/clinicas/${usuario!.clinicaId}`).then(r => r.data),
    enabled: !!usuario?.clinicaId,
    staleTime: 300_000,
  });

  const { data: recetas = [], isLoading } = useQuery({
    queryKey: ['recetas-paciente', id, filterStatus, filterDesde, filterHasta],
    queryFn: () => api.get(`/api/pacientes/${id}/recetas`, {
      params: {
        status:     filterStatus || undefined,
        fechaDesde: filterDesde  || undefined,
        fechaHasta: filterHasta  || undefined,
      },
    }).then(r => r.data as any[]),
  });

  const cancelMutation = useMutation({
    mutationFn: (recetaId: string) => api.patch(`/api/recetas/${recetaId}`, { status: 'cancelada' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recetas-paciente', id] });
      toast.success('Receta cancelada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al cancelar')),
  });

  const activarMutation = useMutation({
    mutationFn: (recetaId: string) => api.post(`/api/recetas/${recetaId}/activar`, { medActivosInactivar: [] }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recetas-paciente', id] });
      qc.invalidateQueries({ queryKey: ['medicamentos-activos', id] });
      toast.success('Receta emitida');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al emitir la receta')),
  });

  function openWizard(recetaId?: string) {
    setEditingId(recetaId || null);
    setWizardOpen(true);
  }

  function closeWizard() {
    setWizardOpen(false);
    setEditingId(null);
    qc.invalidateQueries({ queryKey: ['recetas-paciente', id] });
    qc.invalidateQueries({ queryKey: ['medicamentos-activos', id] });
  }

  const hasFilters = !!(filterStatus || filterDesde || filterHasta);

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-text-secondary">
            <span className="font-semibold text-text-primary">{recetas.length}</span> receta{recetas.length !== 1 ? 's' : ''}
          </p>
          {puedeEmitir && (
            <button onClick={() => openWizard()}
              className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors">
              <Plus size={13} /> Nueva receta
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value as RecetaStatus | '')}
              className="appearance-none border border-gray-200 rounded-lg pl-2.5 pr-6 py-1.5 text-xs bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer">
              <option value="">Todos los estados</option>
              <option value="activa">Activa</option>
              <option value="borrador">Borrador</option>
              <option value="cancelada">Cancelada</option>
            </select>
            <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
          </div>
          <input type="date" value={filterDesde} onChange={e => setFilterDesde(e.target.value)}
            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300" />
          <input type="date" value={filterHasta} onChange={e => setFilterHasta(e.target.value)}
            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300" />
          {hasFilters && (
            <button onClick={() => { setFilterStatus(''); setFilterDesde(''); setFilterHasta(''); }}
              className="text-xs text-primary-600 hover:underline">Limpiar</button>
          )}
        </div>

        {/* List */}
        {isLoading ? (
          <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>
        ) : recetas.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <FileText size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">
              {hasFilters ? 'Sin resultados para los filtros seleccionados' : 'Sin recetas registradas'}
            </p>
            {puedeEmitir && !hasFilters && (
              <button onClick={() => openWizard()}
                className="mt-3 text-xs text-primary-600 hover:underline">Crear primera receta</button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {recetas.map((r: any) => {
              const info = STATUS_INFO[(r.status || 'activa') as RecetaStatus] ?? STATUS_INFO.inactiva;
              return (
                <div key={r.id} className="rounded-xl border border-gray-100 bg-white p-4 space-y-2.5">
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', info.cls)}>{info.label}</span>
                      <span className="text-sm font-semibold text-text-primary">{r.folio}</span>
                    </div>
                    <span className="text-xs text-text-secondary flex-shrink-0">{formatDate(r.fecha)}</span>
                  </div>

                  {/* Doctor */}
                  {r.medico && (
                    <p className="text-xs text-text-secondary">Dr. {r.medico.nombre}</p>
                  )}

                  {/* Diagnosis */}
                  {r.indicaciones && (
                    <p className="text-xs text-text-primary font-medium line-clamp-2">{r.indicaciones}</p>
                  )}

                  {/* Medications */}
                  {r.medicaciones?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {r.medicaciones.map((m: any, i: number) => (
                        <span key={i} className="bg-blue-50 text-blue-700 text-[10px] px-2 py-0.5 rounded-full border border-blue-100">
                          {m.denominacionGenerica}{m.dosis ? ` · ${m.dosis}` : ''}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-1 border-t border-gray-50">
                    {(r.status === 'activa' || r.status === 'inactiva') && (
                      <>
                        <button onClick={() => setPrintReceta(r)}
                          className="flex items-center gap-1 text-[10.5px] text-text-secondary hover:text-text-primary transition-colors">
                          <Printer size={11} /> Ver / Imprimir
                        </button>
                        {r.status === 'activa' && (
                          <button onClick={() => toast.info('Envío por correo electrónico próximamente')}
                            className="flex items-center gap-1 text-[10.5px] text-text-secondary hover:text-text-primary transition-colors">
                            <Mail size={11} /> Enviar
                          </button>
                        )}
                      </>
                    )}
                    {r.status === 'cancelada' && (
                      <button onClick={() => setPrintReceta(r)}
                        className="flex items-center gap-1 text-[10.5px] text-text-secondary hover:text-text-primary transition-colors">
                        <Eye size={11} /> Ver
                      </button>
                    )}
                    {puedeEmitir && r.status === 'borrador' && (
                      <>
                        <button onClick={() => openWizard(r.id)}
                          className="flex items-center gap-1 text-[10.5px] text-primary-600 hover:text-primary-800 transition-colors">
                          <Edit2 size={11} /> Editar
                        </button>
                        <button
                          onClick={() => { if (confirm('¿Emitir esta receta ahora?')) activarMutation.mutate(r.id); }}
                          disabled={activarMutation.isPending}
                          className="flex items-center gap-1 text-[10.5px] text-green-600 hover:text-green-800 disabled:opacity-50 transition-colors">
                          <Send size={11} /> Emitir
                        </button>
                      </>
                    )}
                    {puedeEmitir && !noDelete && r.status !== 'cancelada' && r.status !== 'inactiva' && (
                      <button
                        onClick={() => { if (confirm('¿Cancelar esta receta?')) cancelMutation.mutate(r.id); }}
                        className="flex items-center gap-1 text-[10.5px] text-red-400 hover:text-red-600 transition-colors ml-auto">
                        <X size={11} /> Cancelar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Wizard */}
      {wizardOpen && (
        <RecetaWizard
          pacienteId={id}
          recetaId={editingId}
          paciente={paciente}
          citaId={editingId ? undefined : citaId}
          onClose={closeWizard}
        />
      )}

      {/* Print modal */}
      {printReceta && (
        <RecetaPrintModal
          receta={printReceta}
          paciente={paciente}
          clinica={clinica}
          usuario={usuario}
          onClose={() => setPrintReceta(null)}
        />
      )}
    </>
  );
}
